import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { FormControl, FormField, FormItem } from "@/src/components/ui/form";
import { useEffect } from "react";
import { EditBookingNotesForm } from "./bookingNotes.schema";
import { useBookingNotesMutation } from "@/src/lib/bookingNotesMutation";
import { useToast } from "@/src/components/ui/use-toast";
import { TrashIcon } from "lucide-react";
import { Textarea } from "@/src/components/ui/textarea";

type EditBookingNotesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialNotes: string[] | undefined;
  bookingId: string;
};

export const EditBookingNotesModal = ({ open, setOpen, initialNotes, bookingId }: EditBookingNotesModalProps) => {
  const updateBookingNotes = useBookingNotesMutation(bookingId);
  const { toast } = useToast();

  const form = useForm<EditBookingNotesForm>({
    defaultValues: {
      notes: initialNotes || [""],
    },
  });

  const { handleSubmit, control, reset } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "notes" as never,
  });

  useEffect(() => {
    reset({ notes: initialNotes || [""] });
  }, [initialNotes, reset]);

  const onSubmit = (data: EditBookingNotesForm) => {
    const toastId = toast({
      description: "Updating notes...",
      variant: "default",
      duration: Infinity,
    }).id;

    updateBookingNotes.mutate(
      { notes: data.notes, toastId },
      {
        onSuccess: () => {
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-[640px] max-h-[calc(100%-60px)] overflow-y-auto">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle className="text-3xl font-semi-bold">Edit Notes</DialogTitle>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              {fields.map((field, index) => (
                <FormField
                  key={field.id}
                  control={control}
                  name={`notes.${index}`}
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Textarea {...field} placeholder={`Note ${index + 1}`} />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => remove(index)}
                        disabled={fields.length === 1}
                      >
                        <TrashIcon size={16} />
                      </Button>
                    </FormItem>
                  )}
                />
              ))}
              <Button
                onClick={() => {
                  append("");
                }}
                className="col-start-1"
                type="button"
                variant="secondary"
              >
                Add Another Step
              </Button>
            </div>

            <DialogFooter className="flex mt-8 w-full !justify-between">
              <Button type="button" onClick={() => setOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" variant="default">
                Save Notes
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
